import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { ArrowRight, CheckCircle2, Clock, ChefHat } from 'lucide-react';
import { motion } from 'framer-motion';

const HERO_IMAGE = 'https://images.unsplash.com/photo-1683629357846-30eacff8f615?w=1200&q=85';

export default function CuisinePage() {
  const navigate = useNavigate();

  const included = [
    'Démontage ancienne cuisine',
    'Plomberie et évacuation',
    'Électricité (prises, éclairage, hotte)',
    'Meubles de cuisine',
    'Plan de travail',
    'Électroménager (selon choix)',
    'Crédence',
    'Raccordements finaux'
  ];

  const piloted = [
    'Cuisiniste, plombier, électricien synchronisés',
    'Vérification niveaux et mesures',
    'Livraison meubles coordonnée',
    'Pose et réglages',
    'Tests électriques et eau',
    'Finitions et nettoyage'
  ];

  const steps = [
    { title: 'Prise de mesures', desc: 'Relevés précis, plan 3D si besoin', duration: '1-2 jours' },
    { title: 'Choix matériaux', desc: 'Meubles, plan de travail, électroménager', duration: '1 semaine' },
    { title: 'Démontage', desc: 'Retrait ancien, préparation supports', duration: '1-2 jours' },
    { title: 'Plomberie & électricité', desc: 'Mise aux normes, préparation raccordements', duration: '2-3 jours' },
    { title: 'Pose cuisine', desc: 'Meubles, plan de travail, électroménager', duration: '2-4 jours' },
    { title: 'Finitions', desc: 'Crédence, joints, tests, nettoyage', duration: '1 jour' }
  ];

  const faqs = [
    { q: 'Combien de temps dure une rénovation de cuisine ?', a: 'Entre 2 et 4 semaines selon la complexité. Le délai inclut fabrication et livraison des meubles.' },
    { q: 'Peut-on cuisiner pendant les travaux ?', a: 'Non, la cuisine est inutilisable. Nous organisons le chantier pour minimiser la durée.' },
    { q: 'Électroménager est-il fourni ?', a: 'Vous choisissez. Nous pouvons tout fournir et installer, ou poser vos équipements.' },
    { q: 'Que faire si les murs ne sont pas droits ?', a: 'Nous anticipons avec les mesures précises et ajustons la pose. Pas de mauvaise surprise.' }
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--hr-bg)' }}>
      
      {/* HERO */}
      <section className="relative overflow-hidden hero-gradient">
        <div className="max-w-[1160px] mx-auto px-4 sm:px-6 lg:px-8 pt-16 sm:pt-20 pb-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <Badge 
                className="rounded-full px-4 py-1.5 font-['Montserrat'] text-xs font-medium uppercase"
                style={{ backgroundColor: 'var(--hr-accent)', color: 'var(--hr-accent-fg)' }}
              >
                Service premium
              </Badge>
              
              <h1 
                className="font-['Playfair_Display'] text-4xl sm:text-5xl font-bold leading-tight"
                style={{ color: 'var(--hr-text)' }}
              >
                Rénovation cuisine pilotée
              </h1>
              
              <p className="text-xl leading-relaxed" style={{ color: 'var(--hr-text-muted)' }}>
                Installation complète avec coordination cuisiniste, plombier, électricien. Du démontage aux finitions.
              </p>

              <div className="flex gap-4 pt-4">
                <Button
                  size="lg"
                  className="rounded-xl px-8"
                  style={{ backgroundColor: 'var(--hr-primary)', color: 'var(--hr-primary-fg)' }}
                  onClick={() => navigate('/devis')}
                >
                  Demander un devis
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </div>

              <div className="flex items-center gap-6 pt-4">
                <div className="flex items-center gap-2">
                  <Clock className="h-5 w-5" style={{ color: 'var(--hr-accent)' }} />
                  <span className="text-sm font-medium" style={{ color: 'var(--hr-text)' }}>2-4 semaines</span>
                </div>
                <div className="flex items-center gap-2">
                  <ChefHat className="h-5 w-5" style={{ color: 'var(--hr-accent)' }} />
                  <span className="text-sm font-medium" style={{ color: 'var(--hr-text)' }}>Clé en main</span>
                </div>
              </div>
            </div>

            <div className="media-frame">
              <img src={HERO_IMAGE} alt="Cuisine rénovée" className="w-full h-auto" />
            </div>
          </div>
        </div>
      </section>

      {/* LE PROBLÈME */}
      <section className="py-16 sm:py-20" style={{ backgroundColor: 'var(--hr-bg-alt)' }}>
        <div className="max-w-[1160px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
            <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-semibold text-center mb-12" style={{ color: 'var(--hr-text)' }}>
              Une cuisine sans coordination, c'est le parcours du combattant
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                'Mesures fausses, meubles qui ne rentrent pas',
                'Prises mal placées',
                'Livraison en retard',
                'Cuisiniste et plombier qui se croisent',
                'Plan de travail livré trop tôt',
                'Électroménager impossible à encastrer'
              ].map((pain) => (
                <div key={pain} className="flex items-start gap-3 p-4 rounded-xl" style={{ backgroundColor: 'var(--hr-bg)' }}>
                  <div className="mt-1 h-1.5 w-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: 'var(--hr-danger)' }} />
                  <p className="text-base" style={{ color: 'var(--hr-text)' }}>{pain}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CE QUI EST INCLUS */}
      <section className="py-16 sm:py-20">
        <div className="max-w-[1160px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-semibold mb-4" style={{ color: 'var(--hr-text)' }}>
              Ce qui est inclus
            </h2>
            <p className="text-lg" style={{ color: 'var(--hr-text-muted)' }}>Installation complète clé en main</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {included.map((item) => (
              <Card key={item} className="rounded-2xl border-[color:var(--hr-border)]">
                <CardContent className="p-6 flex items-start gap-3">
                  <CheckCircle2 className="h-6 w-6 mt-0.5 flex-shrink-0" style={{ color: 'var(--hr-success)' }} />
                  <span className="text-sm font-medium" style={{ color: 'var(--hr-text)' }}>{item}</span>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CE QUI EST PILOTÉ */}
      <section className="py-16 sm:py-20" style={{ backgroundColor: 'var(--hr-bg-alt)' }}>
        <div className="max-w-[1160px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="font-['Montserrat'] text-xs font-medium uppercase tracking-wide mb-4" style={{ color: 'var(--hr-accent)' }}>
              Notre différence
            </p>
            <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-semibold" style={{ color: 'var(--hr-text)' }}>
              Ce que nous pilotons pour vous
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {piloted.map((item, i) => (
              <motion.div
                key={item}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="rounded-2xl border-[color:var(--hr-border)] h-full">
                  <CardContent className="p-6 flex items-start gap-3">
                    <div className="h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'var(--hr-primary)' }}>
                      <span className="text-white text-sm font-bold">{i + 1}</span>
                    </div>
                    <span className="text-base" style={{ color: 'var(--hr-text)' }}>{item}</span>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* DÉROULÉ */}
      <section className="py-16 sm:py-20">
        <div className="max-w-[1160px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-semibold mb-4" style={{ color: 'var(--hr-text)' }}>
              Comment ça se déroule
            </h2>
          </div>
          <div className="space-y-4">
            {steps.map((step, i) => (
              <Card key={step.title} className="rounded-2xl border-[color:var(--hr-border)]">
                <CardContent className="p-6 flex items-center gap-6">
                  <div className="h-12 w-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'var(--hr-primary)' }}>
                    <span className="text-white font-bold text-lg">{i + 1}</span>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-['Figtree'] text-lg font-semibold mb-1" style={{ color: 'var(--hr-text)' }}>{step.title}</h3>
                    <p className="text-sm" style={{ color: 'var(--hr-text-muted)' }}>{step.desc}</p>
                  </div>
                  <div className="text-right">
                    <span className="text-sm font-medium" style={{ color: 'var(--hr-accent)' }}>{step.duration}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 sm:py-20" style={{ backgroundColor: 'var(--hr-bg-alt)' }}>
        <div className="max-w-[900px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-['Playfair_Display'] text-3xl font-semibold" style={{ color: 'var(--hr-text)' }}>
              Questions fréquentes
            </h2>
          </div>
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, i) => (
              <AccordionItem 
                key={faq.q} 
                value={`item-${i}`}
                className="rounded-2xl border px-6" 
                style={{ borderColor: 'var(--hr-border)', backgroundColor: 'var(--hr-bg)' }}
              >
                <AccordionTrigger className="text-left hover:no-underline">
                  <span className="font-['Figtree'] font-semibold" style={{ color: 'var(--hr-text)' }}>{faq.q}</span>
                </AccordionTrigger>
                <AccordionContent>
                  <p className="text-sm leading-relaxed pt-2" style={{ color: 'var(--hr-text-muted)' }}>{faq.a}</p>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="py-20" style={{ backgroundColor: 'var(--hr-primary)' }}>
        <div className="max-w-[1160px] mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-6">
          <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-semibold" style={{ color: 'var(--hr-primary-fg)' }}>
            Prêt à rénover votre cuisine ?
          </h2>
          <Button
            size="lg"
            className="rounded-xl px-10 py-6 text-lg font-medium"
            style={{ backgroundColor: 'var(--hr-bg)', color: 'var(--hr-text)' }}
            onClick={() => navigate('/devis')}
          >
            Demander mon devis
            <ArrowRight className="ml-2 h-6 w-6" />
          </Button>
        </div>
      </section>
    </div>
  );
}